import React, { useEffect } from 'react';

export type ToastType = 'success' | 'info' | 'error';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastProps {
  toast: Toast;
  onClose: (id: string) => void;
}

/**
 * Toast notification component
 * Shows success, info, or error messages with auto-dismiss
 */
const ToastComponent: React.FC<ToastProps> = ({ toast, onClose }) => {
  useEffect(() => {
    const duration = toast.duration || 4000;
    const timer = setTimeout(() => {
      onClose(toast.id);
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onClose]);

  const getIconAndStyles = () => {
    switch (toast.type) {
      case 'success':
        return {
          icon: 'check_circle',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          iconColor: 'text-green-600',
          textColor: 'text-green-800'
        };
      case 'info':
        return {
          icon: 'info',
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          iconColor: 'text-blue-600',
          textColor: 'text-blue-800'
        };
      case 'error':
        return {
          icon: 'error',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          iconColor: 'text-red-600',
          textColor: 'text-red-800'
        };
    }
  };

  const styles = getIconAndStyles();

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border ${styles.bgColor} ${styles.borderColor} shadow-lg animate-slide-in-right`}
      role="alert"
      aria-live="polite"
    >
      {/* Icon */}
      <span className={`material-icons ${styles.iconColor} flex-shrink-0`} style={{ fontSize: '20px' }}>
        {styles.icon}
      </span>

      {/* Message */}
      <p className={`flex-1 text-sm font-medium ${styles.textColor}`}>
        {toast.message}
      </p>

      {/* Close button */}
      <button
        onClick={() => onClose(toast.id)}
        className={`${styles.iconColor} hover:opacity-70 transition-opacity flex-shrink-0`}
        aria-label="Close notification"
      >
        <span className="material-icons" style={{ fontSize: '20px' }}>
          close
        </span>
      </button>
    </div>
  );
};

export default ToastComponent;
